import { useState, memo } from "react"
import { Plus } from "@phosphor-icons/react"
import usePagination from "../../hooks/usePagination"
import ApiError from "../../components/ApiError"
import ErrorCodesMappingsTable from "../../components/ErrorCodesMappingsTable"
import ErrorCodesMappingModal from "../../components/ErrorCodesMappingModal"
import ConfirmationDialog from "../../components/ConfirmationDialog"
import {
  useErrorCodesMappingsData,
  useCreateErrorCodesMapping,
  useUpdateErrorCodesMapping,
  useDeleteErrorCodesMapping,
} from "../../hooks/useErrorCodesMappings"

const ErrorCodes = () => {
  const { limit, onPaginationChange, page, pagination } = usePagination()
  const [filters, setFilters] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [deletingItem, setDeletingItem] = useState(null)

  const { data, isLoading, error, isError } = useErrorCodesMappingsData({
    page,
    limit,
    filters: {
      ...filters.reduce((acc, filter) => {
        if (filter.value) {
          acc[filter.id] = filter.value
        }
        return acc
      }, {}),
    },
  })

  const createMutation = useCreateErrorCodesMapping()
  const updateMutation = useUpdateErrorCodesMapping()
  const deleteMutation = useDeleteErrorCodesMapping()

  const handleSetFilters = (newFilters) => {
    setFilters(newFilters)
  }

  const handleCreateNew = () => {
    setEditingItem(null)
    setIsModalOpen(true)
  }

  const handleEdit = (item) => {
    setEditingItem(item)
    setIsModalOpen(true)
  }

  const handleDelete = (item) => {
    setDeletingItem(item)
    setIsConfirmDialogOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingItem(null)
  }

  const handleModalSubmit = async (values) => {
    if (editingItem) {
      await updateMutation.mutateAsync({
        errorId: editingItem.errorId,
        data: values,
      })
    } else {
      await createMutation.mutateAsync(values)
    }
  }

  const handleConfirmDelete = async () => {
    if (deletingItem) {
      try {
        await deleteMutation.mutateAsync(deletingItem.errorId)
        setIsConfirmDialogOpen(false)
        setDeletingItem(null)
      } catch (error) {
        // Error handling will be done by the mutation hook
        console.error("Error deleting item:", error)
      }
    }
  }

  const handleCancelDelete = () => {
    setIsConfirmDialogOpen(false)
    setDeletingItem(null)
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div className="px-1 flex justify-between items-center my-4 mx-4">
        <h1 className="font-medium text-xl text-primary">Error Codes</h1>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={handleCreateNew}
        >
          <Plus size={16} />
          Create New
        </button>
      </div>

      <div className="overflow-x-auto mx-4">
        <ErrorCodesMappingsTable
          data={isLoading ? [] : data?.data || []}
          filters={filters}
          loading={isLoading}
          pageCount={isLoading ? 0 : data?.pages || 0}
          pagination={pagination}
          setFilters={handleSetFilters}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onPaginationChange={onPaginationChange}
        />
      </div>

      <ErrorCodesMappingModal
        initialData={editingItem}
        isEditing={!!editingItem}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />

      <ConfirmationDialog
        cancelText="Cancel"
        confirmText="Delete"
        isOpen={isConfirmDialogOpen}
        message={`Are you sure you want to delete the error code mapping "${deletingItem?.errorCode}"? This action cannot be undone.`}
        title="Delete Error Code Mapping"
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

export default memo(ErrorCodes)
